import { useRef, useMemo } from "react"
import { useFrame } from "@react-three/fiber"
import { Text, MeshTransmissionMaterial, MeshReflectorMaterial, useTexture } from "@react-three/drei"
import type * as THREE from "three"
import { useScene } from "@/contexts/scene-context"

interface EnvironmentStructureProps {
  environment: string
}

export default function EnvironmentStructure({ environment }: EnvironmentStructureProps) {
  const { sceneState } = useScene()
  const groupRef = useRef<THREE.Group>(null)

  // Create environment-specific structure
  const Structure = useMemo(() => {
    switch (environment) {
      case "datacenter":
        return <DataCenterStructure wireframe={sceneState.wireframe} opacity={sceneState.modelOpacity} />
      case "greenhouse":
        return <GreenhouseStructure wireframe={sceneState.wireframe} opacity={sceneState.modelOpacity} />
      case "charging":
        return <ChargingStationStructure wireframe={sceneState.wireframe} opacity={sceneState.modelOpacity} />
      case "smartbuilding":
        return <SmartBuildingStructure wireframe={sceneState.wireframe} opacity={sceneState.modelOpacity} />
      case "industrial":
        return <IndustrialStructure wireframe={sceneState.wireframe} opacity={sceneState.modelOpacity} />
      default:
        return <DataCenterStructure wireframe={sceneState.wireframe} opacity={sceneState.modelOpacity} />
    }
  }, [environment, sceneState.wireframe, sceneState.modelOpacity])

  // Subtle animation
  useFrame((state) => {
    if (groupRef.current) {
      // Very subtle floating effect
      groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.2) * 0.02
    }
  })

  return <group ref={groupRef}>{Structure}</group>
}

// Data Center Structure
function DataCenterStructure({ wireframe, opacity }: { wireframe: boolean; opacity: number }) {
  const floorTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=data center floor texture")
  const wallTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=server room wall texture")

  return (
    <group position={[0, 0, 0]}>
      {/* Floor */}
      <mesh receiveShadow position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[12, 12]} />
        <meshStandardMaterial map={floorTexture} roughness={0.8} metalness={0.2} color="#444444" />
      </mesh>

      {/* Walls */}
      <group position={[0, 1.5, 0]}>
        {/* Back wall */}
        <mesh receiveShadow position={[0, 0, -6]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#555555"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Left wall */}
        <mesh receiveShadow position={[-6, 0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#555555"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Right wall */}
        <mesh receiveShadow position={[6, 0, 0]} rotation={[0, -Math.PI / 2, 0]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#555555"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      </group>

      {/* Server Racks */}
      <ServerRack position={[-3, 1, -4]} wireframe={wireframe} opacity={opacity} />
      <ServerRack position={[0, 1, -4]} wireframe={wireframe} opacity={opacity} />
      <ServerRack position={[3, 1, -4]} wireframe={wireframe} opacity={opacity} />

      <ServerRack position={[-3, 1, 0]} wireframe={wireframe} opacity={opacity} />
      <ServerRack position={[3, 1, 0]} wireframe={wireframe} opacity={opacity} />

      <ServerRack position={[-3, 1, 4]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />
      <ServerRack position={[0, 1, 4]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />
      <ServerRack position={[3, 1, 4]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />

      {/* Ceiling lights */}
      <CeilingLight position={[-3, 2.9, -2]} />
      <CeilingLight position={[3, 2.9, -2]} />
      <CeilingLight position={[-3, 2.9, 2]} />
      <CeilingLight position={[3, 2.9, 2]} />

      {/* Data Center Label */}
      <Text position={[0, 3, -5.9]} fontSize={0.4} color="#ffffff" anchorX="center" anchorY="middle">
        DATA CENTER
      </Text>
    </group>
  )
}

// Greenhouse Structure
function GreenhouseStructure({ wireframe, opacity }: { wireframe: boolean; opacity: number }) {
  const glassRef = useRef<THREE.Mesh>(null)
  const floorTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=soil texture")

  // Animate glass reflections
  useFrame((state) => {
    if (glassRef.current) {
      glassRef.current.rotation.y = state.clock.elapsedTime * 0.01
    }
  })

  return (
    <group position={[0, 0, 0]}>
      {/* Floor/Ground */}
      <mesh receiveShadow position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[12, 12]} />
        <meshStandardMaterial map={floorTexture} color="#3d2817" roughness={1} metalness={0} />
      </mesh>

      {/* Glass Structure */}
      <group position={[0, 2, 0]}>
        {/* Glass Roof */}
        <mesh ref={glassRef} position={[0, 2, 0]} rotation={[Math.PI / 4, 0, 0]} castShadow receiveShadow>
          <boxGeometry args={[10, 0.05, 14.14]} />
          <MeshTransmissionMaterial
            backside
            samples={4}
            thickness={0.5}
            roughness={0.1}
            chromaticAberration={0.02}
            anisotropy={0.1}
            distortion={0.2}
            distortionScale={0.2}
            temporalDistortion={0.1}
            iridescence={0.2}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            wireframe={wireframe}
            transparent
            opacity={opacity * 0.9}
          />
        </mesh>

        {/* Glass Walls */}
        <mesh position={[0, 0, -5]} castShadow receiveShadow>
          <boxGeometry args={[10, 4, 0.05]} />
          <MeshTransmissionMaterial
            backside
            samples={4}
            thickness={0.5}
            roughness={0.1}
            chromaticAberration={0.02}
            anisotropy={0.1}
            distortion={0.2}
            distortionScale={0.2}
            temporalDistortion={0.1}
            iridescence={0.2}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            wireframe={wireframe}
            transparent
            opacity={opacity * 0.9}
          />
        </mesh>

        <mesh position={[0, 0, 5]} castShadow receiveShadow>
          <boxGeometry args={[10, 4, 0.05]} />
          <MeshTransmissionMaterial
            backside
            samples={4}
            thickness={0.5}
            roughness={0.1}
            chromaticAberration={0.02}
            anisotropy={0.1}
            distortion={0.2}
            distortionScale={0.2}
            temporalDistortion={0.1}
            iridescence={0.2}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            wireframe={wireframe}
            transparent
            opacity={opacity * 0.9}
          />
        </mesh>

        <mesh position={[-5, 0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[10, 4, 0.05]} />
          <MeshTransmissionMaterial
            backside
            samples={4}
            thickness={0.5}
            roughness={0.1}
            chromaticAberration={0.02}
            anisotropy={0.1}
            distortion={0.2}
            distortionScale={0.2}
            temporalDistortion={0.1}
            iridescence={0.2}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            wireframe={wireframe}
            transparent
            opacity={opacity * 0.9}
          />
        </mesh>

        <mesh position={[5, 0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow receiveShadow>
          <boxGeometry args={[10, 4, 0.05]} />
          <MeshTransmissionMaterial
            backside
            samples={4}
            thickness={0.5}
            roughness={0.1}
            chromaticAberration={0.02}
            anisotropy={0.1}
            distortion={0.2}
            distortionScale={0.2}
            temporalDistortion={0.1}
            iridescence={0.2}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            wireframe={wireframe}
            transparent
            opacity={opacity * 0.9}
          />
        </mesh>
      </group>

      {/* Plant Rows */}
      <PlantRow position={[-3, 0, -3]} />
      <PlantRow position={[0, 0, -3]} />
      <PlantRow position={[3, 0, -3]} />

      <PlantRow position={[-3, 0, 0]} />
      <PlantRow position={[0, 0, 0]} />
      <PlantRow position={[3, 0, 0]} />

      <PlantRow position={[-3, 0, 3]} />
      <PlantRow position={[0, 0, 3]} />
      <PlantRow position={[3, 0, 3]} />

      {/* Greenhouse Label */}
      <Text position={[0, 4.5, 0]} fontSize={0.4} color="#ffffff" anchorX="center" anchorY="middle">
        GREENHOUSE
      </Text>
    </group>
  )
}

// Charging Station Structure
function ChargingStationStructure({ wireframe, opacity }: { wireframe: boolean; opacity: number }) {
  const floorTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=concrete texture")

  return (
    <group position={[0, 0, 0]}>
      {/* Floor */}
      <mesh receiveShadow position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[15, 15]} />
        <MeshReflectorMaterial
          blur={[300, 100]}
          resolution={1024}
          mixBlur={1}
          mixStrength={40}
          roughness={1}
          depthScale={1.2}
          minDepthThreshold={0.4}
          maxDepthThreshold={1.4}
          color="#999999"
          metalness={0.5}
          mirror={0.5}
        />
      </mesh>

      {/* Charging Station Canopy */}
      <group position={[0, 3, 0]}>
        <mesh castShadow receiveShadow position={[0, 0, 0]}>
          <boxGeometry args={[10, 0.2, 8]} />
          <meshStandardMaterial
            color="#444444"
            roughness={0.5}
            metalness={0.8}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Support Pillars */}
        <mesh castShadow position={[-4, -1.5, -3]}>
          <cylinderGeometry args={[0.2, 0.2, 3, 8]} />
          <meshStandardMaterial
            color="#777777"
            roughness={0.2}
            metalness={0.9}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        <mesh castShadow position={[4, -1.5, -3]}>
          <cylinderGeometry args={[0.2, 0.2, 3, 8]} />
          <meshStandardMaterial
            color="#777777"
            roughness={0.2}
            metalness={0.9}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        <mesh castShadow position={[-4, -1.5, 3]}>
          <cylinderGeometry args={[0.2, 0.2, 3, 8]} />
          <meshStandardMaterial
            color="#777777"
            roughness={0.2}
            metalness={0.9}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        <mesh castShadow position={[4, -1.5, 3]}>
          <cylinderGeometry args={[0.2, 0.2, 3, 8]} />
          <meshStandardMaterial
            color="#777777"
            roughness={0.2}
            metalness={0.9}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      </group>

      {/* Charging Stations */}
      <ChargingUnit position={[-3, 0, -2]} wireframe={wireframe} opacity={opacity} />
      <ChargingUnit position={[0, 0, -2]} wireframe={wireframe} opacity={opacity} />
      <ChargingUnit position={[3, 0, -2]} wireframe={wireframe} opacity={opacity} />

      <ChargingUnit position={[-3, 0, 2]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />
      <ChargingUnit position={[0, 0, 2]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />
      <ChargingUnit position={[3, 0, 2]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />

      {/* Charging Station Label */}
      <Text position={[0, 3.5, 0]} fontSize={0.4} color="#ffffff" anchorX="center" anchorY="middle">
        EV CHARGING STATION
      </Text>
    </group>
  )
}

// Smart Building Structure
function SmartBuildingStructure({ wireframe, opacity }: { wireframe: boolean; opacity: number }) {
  const floorTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=wooden floor texture")
  const wallTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=wall texture")

  return (
    <group position={[0, 0, 0]}>
      {/* Floor */}
      <mesh receiveShadow position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[12, 12]} />
        <meshStandardMaterial map={floorTexture} color="#a88c6d" roughness={0.8} metalness={0.1} />
      </mesh>

      {/* Walls */}
      <group position={[0, 1.5, 0]}>
        {/* Back wall */}
        <mesh receiveShadow position={[0, 0, -6]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#e0e0e0"
            roughness={0.9}
            metalness={0.1}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Left wall */}
        <mesh receiveShadow position={[-6, 0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#e0e0e0"
            roughness={0.9}
            metalness={0.1}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Right wall */}
        <mesh receiveShadow position={[6, 0, 0]} rotation={[0, -Math.PI / 2, 0]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#e0e0e0"
            roughness={0.9}
            metalness={0.1}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Front wall with window */}
        <mesh receiveShadow position={[0, 0, 6]} castShadow>
          <boxGeometry args={[12, 3, 0.1]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#e0e0e0"
            roughness={0.9}
            metalness={0.1}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Window */}
        <mesh position={[0, 0, 6.06]}>
          <boxGeometry args={[4, 1.5, 0.1]} />
          <MeshTransmissionMaterial
            backside
            samples={4}
            thickness={0.5}
            roughness={0}
            chromaticAberration={0.02}
            anisotropy={0.1}
            distortion={0.2}
            distortionScale={0.2}
            temporalDistortion={0.1}
            iridescence={0.2}
            iridescenceIOR={1}
            iridescenceThicknessRange={[0, 1400]}
            wireframe={wireframe}
            transparent
            opacity={opacity * 0.9}
          />
        </mesh>
      </group>

      {/* Furniture */}
      <Furniture position={[0, 0, 0]} wireframe={wireframe} opacity={opacity} />

      {/* Ceiling lights */}
      <CeilingLight position={[-3, 2.9, -2]} />
      <CeilingLight position={[3, 2.9, -2]} />
      <CeilingLight position={[-3, 2.9, 2]} />
      <CeilingLight position={[3, 2.9, 2]} />

      {/* Smart Building Label */}
      <Text position={[0, 3, -5.9]} fontSize={0.4} color="#ffffff" anchorX="center" anchorY="middle">
        SMART BUILDING
      </Text>
    </group>
  )
}

// Industrial Structure
function IndustrialStructure({ wireframe, opacity }: { wireframe: boolean; opacity: number }) {
  const floorTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=concrete industrial floor texture")
  const wallTexture = useTexture("/placeholder.svg?height=1024&width=1024&query=industrial wall texture")

  return (
    <group position={[0, 0, 0]}>
      {/* Floor */}
      <mesh receiveShadow position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[15, 15]} />
        <meshStandardMaterial map={floorTexture} color="#555555" roughness={0.9} metalness={0.2} />
      </mesh>

      {/* Walls */}
      <group position={[0, 3, 0]}>
        {/* Back wall */}
        <mesh receiveShadow position={[0, 0, -7.5]} castShadow>
          <boxGeometry args={[15, 6, 0.2]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#777777"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Left wall */}
        <mesh receiveShadow position={[-7.5, 0, 0]} rotation={[0, Math.PI / 2, 0]} castShadow>
          <boxGeometry args={[15, 6, 0.2]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#777777"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Right wall */}
        <mesh receiveShadow position={[7.5, 0, 0]} rotation={[0, -Math.PI / 2, 0]} castShadow>
          <boxGeometry args={[15, 6, 0.2]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#777777"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Front wall with large door */}
        <mesh receiveShadow position={[0, 0, 7.5]} castShadow>
          <boxGeometry args={[15, 6, 0.2]} />
          <meshStandardMaterial
            map={wallTexture}
            color="#777777"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Large industrial door */}
        <mesh position={[0, -1, 7.6]}>
          <boxGeometry args={[4, 4, 0.1]} />
          <meshStandardMaterial
            color="#444444"
            roughness={0.5}
            metalness={0.7}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      </group>

      {/* Roof trusses */}
      <group position={[0, 6, 0]}>
        <mesh castShadow>
          <boxGeometry args={[15, 0.3, 15]} />
          <meshStandardMaterial
            color="#555555"
            roughness={0.7}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>

        {/* Roof supports */}
        {[-6, -3, 0, 3, 6].map((x, i) => (
          <mesh key={i} position={[x, -1.5, 0]} castShadow>
            <boxGeometry args={[0.3, 3, 15]} />
            <meshStandardMaterial
              color="#666666"
              roughness={0.5}
              metalness={0.5}
              wireframe={wireframe}
              transparent={opacity < 1}
              opacity={opacity}
            />
          </mesh>
        ))}
      </group>

      {/* Industrial Equipment */}
      <IndustrialMachine position={[-4, 0, -4]} wireframe={wireframe} opacity={opacity} />
      <IndustrialMachine position={[4, 0, -4]} wireframe={wireframe} opacity={opacity} />
      <IndustrialMachine position={[-4, 0, 4]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />
      <IndustrialMachine position={[4, 0, 4]} wireframe={wireframe} opacity={opacity} rotation={[0, Math.PI, 0]} />

      {/* Ceiling lights - industrial style */}
      <IndustrialLight position={[-4, 5.9, -4]} />
      <IndustrialLight position={[4, 5.9, -4]} />
      <IndustrialLight position={[-4, 5.9, 4]} />
      <IndustrialLight position={[4, 5.9, 4]} />
      <IndustrialLight position={[0, 5.9, 0]} />

      {/* Industrial Facility Label */}
      <Text position={[0, 6.2, 0]} fontSize={0.5} color="#ffffff" anchorX="center" anchorY="middle">
        INDUSTRIAL FACILITY
      </Text>
    </group>
  )
}

// Server Rack Component
function ServerRack({ position = [0, 0, 0], rotation = [0, 0, 0], wireframe = false, opacity = 1 }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Rack Frame */}
      <mesh castShadow receiveShadow position={[0, 1, 0]}>
        <boxGeometry args={[1, 2, 0.8]} />
        <meshStandardMaterial
          color="#111111"
          roughness={0.5}
          metalness={0.8}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Server Units */}
      {Array.from({ length: 5 }).map((_, i) => (
        <mesh key={i} castShadow receiveShadow position={[0, 0.4 + i * 0.3, 0.05]}>
          <boxGeometry args={[0.9, 0.25, 0.7]} />
          <meshStandardMaterial
            color={i % 2 === 0 ? "#333333" : "#222222"}
            roughness={0.4}
            metalness={0.9}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      ))}

      {/* Server LEDs */}
      {Array.from({ length: 5 }).map((_, i) => (
        <group key={i} position={[0.4, 0.4 + i * 0.3, 0.41]}>
          <mesh>
            <sphereGeometry args={[0.02, 8, 8]} />
            <meshStandardMaterial
              color={Math.random() > 0.7 ? "#ff0000" : "#00ff00"}
              emissive={Math.random() > 0.7 ? "#ff0000" : "#00ff00"}
              emissiveIntensity={2}
            />
          </mesh>
          <pointLight color={Math.random() > 0.7 ? "#ff0000" : "#00ff00"} intensity={0.2} distance={0.5} />
        </group>
      ))}
    </group>
  )
}

// Ceiling Light Component
function CeilingLight({ position = [0, 0, 0] }) {
  return (
    <group position={position}>
      <mesh castShadow>
        <boxGeometry args={[0.6, 0.1, 0.6]} />
        <meshStandardMaterial color="#111111" roughness={0.5} metalness={0.5} />
      </mesh>
      <mesh position={[0, -0.05, 0]}>
        <boxGeometry args={[0.5, 0.02, 0.5]} />
        <meshStandardMaterial color="#ffffff" emissive="#ffffff" emissiveIntensity={1} />
      </mesh>
      <pointLight color="#ffffff" intensity={0.8} distance={8} decay={2} />
    </group>
  )
}

// Industrial Light Component
function IndustrialLight({ position = [0, 0, 0] }) {
  return (
    <group position={position}>
      <mesh castShadow>
        <cylinderGeometry args={[0.2, 0.2, 0.1, 8]} />
        <meshStandardMaterial color="#111111" roughness={0.5} metalness={0.8} />
      </mesh>
      <mesh position={[0, -0.2, 0]}>
        <cylinderGeometry args={[0.15, 0.25, 0.3, 8]} />
        <meshStandardMaterial color="#dddddd" emissive="#ffffff" emissiveIntensity={0.5} />
      </mesh>
      <spotLight
        color="#ffffff"
        intensity={1}
        distance={10}
        angle={0.5}
        penumbra={0.5}
        decay={2}
        position={[0, -0.2, 0]}
        castShadow
      />
    </group>
  )
}

// Plant Row Component
function PlantRow({ position = [0, 0, 0] }) {
  return (
    <group position={position}>
      <mesh castShadow receiveShadow position={[0, 0.1, 0]}>
        <boxGeometry args={[1.8, 0.2, 0.8]} />
        <meshStandardMaterial color="#5d4037" roughness={0.9} metalness={0.1} />
      </mesh>

      {/* Plants */}
      {Array.from({ length: 5 }).map((_, i) => (
        <group key={i} position={[(i - 2) * 0.3, 0.3, 0]}>
          {/* Plant stem */}
          <mesh castShadow>
            <cylinderGeometry args={[0.01, 0.01, 0.3, 8]} />
            <meshStandardMaterial color="#2e7d32" roughness={0.8} metalness={0.1} />
          </mesh>

          {/* Plant leaves */}
          <mesh castShadow position={[0, 0.2, 0]}>
            <sphereGeometry args={[0.1, 8, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color="#4caf50" roughness={0.9} metalness={0.1} />
          </mesh>
        </group>
      ))}
    </group>
  )
}

// Charging Unit Component
function ChargingUnit({ position = [0, 0, 0], rotation = [0, 0, 0], wireframe = false, opacity = 1 }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Charging Station Base */}
      <mesh castShadow receiveShadow position={[0, 0.5, 0]}>
        <boxGeometry args={[1, 1, 0.8]} />
        <meshStandardMaterial
          color="#444444"
          roughness={0.5}
          metalness={0.7}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Charging Station Display */}
      <mesh castShadow receiveShadow position={[0, 0.8, 0.41]}>
        <boxGeometry args={[0.6, 0.4, 0.02]} />
        <meshStandardMaterial
          color="#111111"
          roughness={0.2}
          metalness={0.8}
          emissive="#222222"
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Charging Cable */}
      <mesh castShadow position={[0.4, 0.5, 0]}>
        <cylinderGeometry args={[0.05, 0.05, 0.8, 8]} rotation={[0, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#111111"
          roughness={0.8}
          metalness={0.2}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Charging Connector */}
      <mesh castShadow position={[0.8, 0.5, 0]}>
        <boxGeometry args={[0.2, 0.15, 0.1]} />
        <meshStandardMaterial
          color="#222222"
          roughness={0.5}
          metalness={0.5}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Status Light */}
      <mesh position={[0, 1.1, 0.41]}>
        <sphereGeometry args={[0.05, 8, 8]} />
        <meshStandardMaterial
          color="#00ff00"
          emissive="#00ff00"
          emissiveIntensity={2}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
      <pointLight position={[0, 1.1, 0.41]} color="#00ff00" intensity={0.5} distance={1} />
    </group>
  )
}

// Furniture Component
function Furniture({ position = [0, 0, 0], wireframe = false, opacity = 1 }) {
  return (
    <group position={position}>
      {/* Sofa */}
      <mesh castShadow receiveShadow position={[0, 0.4, 3]}>
        <boxGeometry args={[3, 0.8, 1]} />
        <meshStandardMaterial
          color="#3949ab"
          roughness={0.9}
          metalness={0.1}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      <mesh castShadow receiveShadow position={[0, 0.9, 3.45]}>
        <boxGeometry args={[3, 0.6, 0.1]} />
        <meshStandardMaterial
          color="#3949ab"
          roughness={0.9}
          metalness={0.1}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Coffee Table */}
      <mesh castShadow receiveShadow position={[0, 0.3, 1.5]}>
        <boxGeometry args={[1.5, 0.1, 0.8]} />
        <meshStandardMaterial
          color="#795548"
          roughness={0.6}
          metalness={0.2}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Table Legs */}
      {[
        [-0.6, -0.2, 1.1],
        [0.6, -0.2, 1.1],
        [-0.6, -0.2, 1.9],
        [0.6, -0.2, 1.9],
      ].map((pos, i) => (
        <mesh key={i} castShadow position={pos}>
          <cylinderGeometry args={[0.05, 0.05, 0.4, 8]} />
          <meshStandardMaterial
            color="#5d4037"
            roughness={0.6}
            metalness={0.3}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      ))}

      {/* TV Unit */}
      <mesh castShadow receiveShadow position={[0, 1, -3]}>
        <boxGeometry args={[3, 2, 0.2]} />
        <meshStandardMaterial
          color="#212121"
          roughness={0.5}
          metalness={0.5}
          emissive="#333333"
          emissiveIntensity={0.2}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* TV Stand */}
      <mesh castShadow receiveShadow position={[0, 0.4, -2.8]}>
        <boxGeometry args={[1.5, 0.8, 0.4]} />
        <meshStandardMaterial
          color="#424242"
          roughness={0.5}
          metalness={0.3}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
    </group>
  )
}

// Industrial Machine Component
function IndustrialMachine({ position = [0, 0, 0], rotation = [0, 0, 0], wireframe = false, opacity = 1 }) {
  return (
    <group position={position} rotation={rotation}>
      {/* Machine Base */}
      <mesh castShadow receiveShadow position={[0, 0.75, 0]}>
        <boxGeometry args={[2, 1.5, 1.5]} />
        <meshStandardMaterial
          color="#455a64"
          roughness={0.6}
          metalness={0.7}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Machine Top */}
      <mesh castShadow receiveShadow position={[0, 1.75, 0]}>
        <boxGeometry args={[1.8, 0.5, 1.3]} />
        <meshStandardMaterial
          color="#37474f"
          roughness={0.5}
          metalness={0.8}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Control Panel */}
      <mesh castShadow receiveShadow position={[0, 1.5, 0.8]}>
        <boxGeometry args={[1, 0.6, 0.1]} />
        <meshStandardMaterial
          color="#263238"
          roughness={0.3}
          metalness={0.9}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Display Screen */}
      <mesh position={[0, 1.5, 0.86]}>
        <planeGeometry args={[0.8, 0.4]} />
        <meshStandardMaterial
          color="#0d47a1"
          emissive="#0d47a1"
          emissiveIntensity={0.5}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Control Buttons */}
      {[-0.3, 0, 0.3].map((x, i) => (
        <mesh key={i} position={[x, 1.3, 0.86]}>
          <cylinderGeometry args={[0.05, 0.05, 0.02, 16]} rotation={[Math.PI / 2, 0, 0]} />
          <meshStandardMaterial
            color={["#f44336", "#ffeb3b", "#4caf50"][i]}
            emissive={["#f44336", "#ffeb3b", "#4caf50"][i]}
            emissiveIntensity={0.5}
            wireframe={wireframe}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
      ))}

      {/* Pipes */}
      <mesh castShadow position={[-0.8, 0.3, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 1.5, 8]} rotation={[0, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#b0bec5"
          roughness={0.4}
          metalness={0.8}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      <mesh castShadow position={[0.8, 0.3, 0]}>
        <cylinderGeometry args={[0.1, 0.1, 1.5, 8]} rotation={[0, 0, Math.PI / 2]} />
        <meshStandardMaterial
          color="#b0bec5"
          roughness={0.4}
          metalness={0.8}
          wireframe={wireframe}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>
    </group>
  )
}
